import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AddLinkOutlinedIcon from '@mui/icons-material/AddLinkOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import axios from '../../helpers/axios';

const cleanId = (value) => String(value?._id || value?.id || value || '').trim();
const cleanSessionId = (value) => String(value || '').trim().toUpperCase();

const getError = (error, fallback) => (
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback
);

const formatDate = (value) => {
  if (!value) return 'без ограничения';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'без ограничения';

  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const buildAbsoluteLink = (linkPath) => {
  if (!linkPath) return '';
  if (/^https?:\/\//i.test(linkPath)) return linkPath;

  const origin = process.env.REACT_APP_FRONT_ORIGIN ||
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:4000'
      : 'https://alotb.ru');

  return `${String(origin).replace(/\/+$/, '')}${linkPath.startsWith('/') ? '' : '/'}${linkPath}`;
};

const initialForm = {
  officeId: '',
  salesSessionId: '',
  title: '',
  customerName: '',
  customerEmail: '',
  duration: '72',
  pinEnabled: false,
  pin: '',
  requiresActiveOfficeSubscription: false,
  accessMode: 'view_only',
  catalogMode: 'none',
  allowedCategoryIds: [],
  walkMode: true,
  createScreenshots: true,
  downloadScreenshots: true,
  viewPrices: false,
  allowRegistrationClaim: true,
  changeQuantity: false,
  useArrayModifier: false,
  useMirrorModifier: false,
  maxArrayCount: 10,
  catalogPageSize: 12,
  maxUses: 0,
};

const SceneGuestAccessAdminPage = () => {
  const [offices, setOffices] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [accesses, setAccesses] = useState([]);
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [loadingOffices, setLoadingOffices] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingAccesses, setLoadingAccesses] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [creating, setCreating] = useState(false);
  const [workingId, setWorkingId] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [createdSecret, setCreatedSecret] = useState(null);

  const selectedOffice = useMemo(
    () => offices.find((office) => cleanId(office) === form.officeId) || null,
    [form.officeId, offices]
  );

  const selectedSession = useMemo(
    () => sessions.find((session) => cleanSessionId(session?.sessionId) === form.salesSessionId) || null,
    [form.salesSessionId, sessions]
  );

  const catalogCategories = Array.isArray(options?.catalog?.categories)
    ? options.catalog.categories
    : [];
  const miniCalculator = form.accessMode === 'mini_calculator';

  const setField = (name, value) => {
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const loadOffices = useCallback(async () => {
    setLoadingOffices(true);
    setError('');

    try {
      const response = await axios.get('/office/get');
      const rows = Array.isArray(response.data?.offices) ? response.data.offices : [];
      setOffices(rows);

      setForm((current) => {
        if (current.officeId || !rows.length) return current;
        return { ...current, officeId: cleanId(rows[0]) };
      });
    } catch (requestError) {
      setError(getError(requestError, 'Не удалось загрузить офисы'));
    } finally {
      setLoadingOffices(false);
    }
  }, []);

  const loadSessions = useCallback(async (officeId) => {
    if (!officeId) {
      setSessions([]);
      return;
    }

    setLoadingSessions(true);
    setError('');

    try {
      const response = await axios.get(`/office-sales-session/office/${officeId}`, {
        params: { limit: 100 },
      });
      const rows = Array.isArray(response.data?.sessions) ? response.data.sessions : [];
      setSessions(rows);

      setForm((current) => {
        const currentExists = rows.some(
          (session) => cleanSessionId(session?.sessionId) === current.salesSessionId
        );
        if (currentExists) return current;

        const first = rows[0] || null;
        return {
          ...current,
          salesSessionId: cleanSessionId(first?.sessionId),
          title: first?.title ? `Просмотр: ${first.title}` : '',
          customerName: first?.customerName || '',
          customerEmail: first?.customerEmail || '',
        };
      });
    } catch (requestError) {
      setSessions([]);
      setError(getError(requestError, 'Не удалось загрузить сессии офиса'));
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  const loadAccesses = useCallback(async (officeId, salesSessionId = '') => {
    if (!officeId) {
      setAccesses([]);
      return;
    }

    setLoadingAccesses(true);
    setError('');

    try {
      const response = await axios.get(`/scene-guest-access/office/${officeId}`, {
        params: {
          limit: 200,
          salesSessionId: salesSessionId || undefined,
        },
      });
      setAccesses(Array.isArray(response.data?.accesses) ? response.data.accesses : []);
    } catch (requestError) {
      setAccesses([]);
      setError(getError(requestError, 'Не удалось загрузить гостевые доступы'));
    } finally {
      setLoadingAccesses(false);
    }
  }, []);

  const loadOptions = useCallback(async (officeId, salesSessionId) => {
    if (!officeId || !salesSessionId) {
      setOptions(null);
      return;
    }

    setLoadingOptions(true);
    setError('');

    try {
      const response = await axios.get(
        `/scene-guest-access/options/${officeId}/${encodeURIComponent(salesSessionId)}`
      );
      const nextOptions = response.data || null;
      setOptions(nextOptions);

      const allCategoryIds = Array.isArray(nextOptions?.catalog?.categoryIds)
        ? nextOptions.catalog.categoryIds.map(cleanId).filter(Boolean)
        : [];

      setForm((current) => ({
        ...current,
        allowedCategoryIds: current.accessMode === 'mini_calculator'
          ? allCategoryIds
          : current.allowedCategoryIds,
      }));
    } catch (requestError) {
      setOptions(null);
      setError(getError(requestError, 'Не удалось прочитать состав сохранённой сцены'));
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    loadOffices();
  }, [loadOffices]);

  useEffect(() => {
    loadSessions(form.officeId);
    loadAccesses(form.officeId, '');
    setOptions(null);
  }, [form.officeId, loadAccesses, loadSessions]);

  useEffect(() => {
    if (!form.officeId || !form.salesSessionId) return;
    loadAccesses(form.officeId, form.salesSessionId);
    loadOptions(form.officeId, form.salesSessionId);
  }, [form.officeId, form.salesSessionId, loadAccesses, loadOptions]);

  const selectSession = (sessionId) => {
    const clean = cleanSessionId(sessionId);
    const session = sessions.find((item) => cleanSessionId(item?.sessionId) === clean) || null;

    setForm((current) => ({
      ...current,
      salesSessionId: clean,
      title: session?.title ? `Просмотр: ${session.title}` : current.title,
      customerName: session?.customerName || current.customerName,
      customerEmail: session?.customerEmail || current.customerEmail,
    }));
  };

  const setAccessMode = (accessMode) => {
    const nextMini = accessMode === 'mini_calculator';
    const categoryIds = catalogCategories.map(cleanId).filter(Boolean);

    setForm((current) => ({
      ...current,
      accessMode: nextMini ? 'mini_calculator' : 'view_only',
      catalogMode: nextMini ? 'office_categories' : 'none',
      allowedCategoryIds: nextMini
        ? (current.allowedCategoryIds.length ? current.allowedCategoryIds : categoryIds)
        : [],
      changeQuantity: nextMini ? current.changeQuantity : false,
      useArrayModifier: nextMini ? current.useArrayModifier : false,
      useMirrorModifier: nextMini ? current.useMirrorModifier : false,
    }));
  };

  const toggleCategory = (categoryId) => {
    setForm((current) => {
      const selected = new Set(current.allowedCategoryIds.map(cleanId));
      if (selected.has(categoryId)) selected.delete(categoryId);
      else selected.add(categoryId);

      return { ...current, allowedCategoryIds: Array.from(selected) };
    });
  };

  const createAccess = async (event) => {
    event.preventDefault();

    if (!form.officeId || !form.salesSessionId || creating) return;

    if (!options?.scene || !Number(options.scene.productIds?.length || 0)) {
      setError('В выбранной сессии нет сохранённой сцены с товарами.');
      return;
    }

    if (miniCalculator && !form.allowedCategoryIds.length) {
      setError('Для мини-калькулятора выберите хотя бы одну категорию.');
      return;
    }

    setCreating(true);
    setError('');
    setNotice('');
    setCreatedSecret(null);

    try {
      const noExpiry = form.duration === 'none';
      const response = await axios.post('/scene-guest-access', {
        officeId: form.officeId,
        salesSessionId: form.salesSessionId,
        title: form.title,
        customerName: form.customerName,
        customerEmail: form.customerEmail,
        pin: form.pinEnabled ? form.pin.trim() : '',
        noExpiry,
        expiresAt: noExpiry ? null : undefined,
        expiresInHours: noExpiry ? undefined : Number(form.duration || 72),
        requiresActiveOfficeSubscription: form.requiresActiveOfficeSubscription,
        accessMode: form.accessMode,
        catalogMode: miniCalculator ? 'office_categories' : 'none',
        allowedCategoryIds: miniCalculator ? form.allowedCategoryIds : [],
        permissions: {
          viewScene: true,
          walkMode: form.walkMode,
          createScreenshots: form.createScreenshots,
          downloadScreenshots: form.createScreenshots && form.downloadScreenshots,
          viewPrices: form.viewPrices,
          browseCatalog: miniCalculator,
          previewCatalogModels: miniCalculator,
          moveObjects: false,
          rotateObjects: false,
          scaleObjects: false,
          useArrayModifier: miniCalculator && form.useArrayModifier,
          useMirrorModifier: miniCalculator && form.useMirrorModifier,
          changeQuantity: miniCalculator && form.changeQuantity,
          changeMaterials: false,
          addProducts: false,
          removeProducts: false,
          submitRequest: true,
        },
        limits: {
          minArrayCount: 1,
          maxArrayCount: Number(form.maxArrayCount || 10),
          maxSceneObjects: 100,
          catalogPageSize: Number(form.catalogPageSize || 12),
        },
        allowAnonymous: true,
        allowRegistrationClaim: form.allowRegistrationClaim,
        maxUses: Math.max(0, Number(form.maxUses || 0)),
      });

      setCreatedSecret({
        link: buildAbsoluteLink(response.data?.linkPath),
        code: response.data?.code || '',
        pin: form.pinEnabled ? form.pin.trim() : '',
      });
      setNotice(response.data?.message || 'Гостевой доступ создан');
      await loadAccesses(form.officeId, form.salesSessionId);
    } catch (requestError) {
      setError(getError(requestError, 'Не удалось создать гостевой доступ'));
    } finally {
      setCreating(false);
    }
  };

  const copyText = async (value, message) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setNotice(message);
    } catch (_error) {
      setError('Браузер не разрешил копирование. Выделите значение вручную.');
    }
  };

  const rotateAccess = async (item) => {
    const id = cleanId(item);
    if (!id || workingId) return;

    setWorkingId(id);
    setError('');
    setNotice('');

    try {
      const response = await axios.patch(`/scene-guest-access/${id}`, {
        patch: { rotateAccessKey: true },
      });

      setCreatedSecret({
        link: buildAbsoluteLink(response.data?.linkPath),
        code: response.data?.code || '',
        pin: '',
      });
      setNotice('Старая ссылка отключена. Созданы новый код и ссылка.');
      await loadAccesses(form.officeId, form.salesSessionId);
    } catch (requestError) {
      setError(getError(requestError, 'Не удалось заменить ссылку'));
    } finally {
      setWorkingId('');
    }
  };

  const revokeAccess = async (item) => {
    const id = cleanId(item);
    if (!id || workingId) return;

    setWorkingId(id);
    setError('');
    setNotice('');

    try {
      await axios.post(`/scene-guest-access/${id}/revoke`);
      setNotice('Гостевой доступ отозван');
      await loadAccesses(form.officeId, form.salesSessionId);
    } catch (requestError) {
      setError(getError(requestError, 'Не удалось отозвать доступ'));
    } finally {
      setWorkingId('');
    }
  };

  const activeCount = accesses.filter((item) => item.status === 'active').length;
  const sceneProductCount = Number(options?.scene?.productIds?.length || 0);
  const initialProductCount = Number(options?.scene?.initialProductIds?.length || 0);

  return (
    <main className="sceneGuestAdmin">
      <header className="sceneGuestAdmin__hero">
        <div className="sceneGuestAdmin__heroIcon"><AdminPanelSettingsOutlinedIcon /></div>
        <div>
          <span>Глобальная админка</span>
          <h1>Гостевой доступ к 3D-сценам</h1>
          <p>
            Администратор платформы выдаёт доступ без оплаты. Можно открыть только готовую
            сцену либо добавить постраничный каталог выбранных категорий офиса.
          </p>
        </div>
        <button type="button" onClick={loadOffices} disabled={loadingOffices}>
          <RefreshOutlinedIcon />
          Обновить офисы
        </button>
      </header>

      {error ? <div className="sceneGuestAdmin__alert is-error">{error}</div> : null}
      {notice ? <div className="sceneGuestAdmin__alert is-success">{notice}</div> : null}

      <section className="sceneGuestAdmin__summary">
        <div><span>Офис</span><strong>{selectedOffice?.name || selectedOffice?.title || 'не выбран'}</strong></div>
        <div><span>Сессия</span><strong>{selectedSession?.sessionId || 'не выбрана'}</strong></div>
        <div><span>Товаров сцены</span><strong>{loadingOptions ? '…' : sceneProductCount}</strong></div>
        <div><span>Сразу загрузятся</span><strong>{loadingOptions ? '…' : initialProductCount}</strong></div>
        <div><span>Категорий офиса</span><strong>{loadingOptions ? '…' : catalogCategories.length}</strong></div>
        <div><span>Активные ссылки</span><strong>{activeCount}</strong></div>
      </section>

      {createdSecret ? (
        <section className="sceneGuestAdmin__secret">
          <div>
            <span>Новая ссылка</span>
            <input readOnly value={createdSecret.link} />
            <button type="button" onClick={() => copyText(createdSecret.link, 'Ссылка скопирована')}>
              <ContentCopyOutlinedIcon />Копировать
            </button>
          </div>
          <div>
            <span>Код</span>
            <input readOnly value={createdSecret.code} />
            <button type="button" onClick={() => copyText(createdSecret.code, 'Код скопирован')}>
              <ContentCopyOutlinedIcon />Копировать
            </button>
          </div>
          {createdSecret.pin ? (
            <div>
              <span>PIN</span>
              <input readOnly value={createdSecret.pin} />
              <button type="button" onClick={() => copyText(createdSecret.pin, 'PIN скопирован')}>
                <ContentCopyOutlinedIcon />Копировать
              </button>
            </div>
          ) : null}
          <small>Полные секреты показываются только после создания или замены.</small>
        </section>
      ) : null}

      <div className="sceneGuestAdmin__layout">
        <form className="sceneGuestAdmin__form" onSubmit={createAccess}>
          <h2>Создать доступ</h2>

          <label>
            <span>Офис</span>
            <select
              value={form.officeId}
              onChange={(event) => setForm((current) => ({ ...current, officeId: event.target.value, salesSessionId: '' }))}
              disabled={loadingOffices}
              required
            >
              <option value="">Выберите офис</option>
              {offices.map((office) => (
                <option key={cleanId(office)} value={cleanId(office)}>
                  {office.name || office.title || cleanId(office)}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Сессия с сохранённой 3D-сценой</span>
            <select
              value={form.salesSessionId}
              onChange={(event) => selectSession(event.target.value)}
              disabled={!form.officeId || loadingSessions}
              required
            >
              <option value="">Выберите сессию</option>
              {sessions.map((session) => (
                <option key={session._id || session.sessionId} value={cleanSessionId(session.sessionId)}>
                  {session.sessionId} — {session.title || 'Без названия'}
                </option>
              ))}
            </select>
          </label>

          <div className="sceneGuestAdmin__modeGrid">
            <button
              type="button"
              className={form.accessMode === 'view_only' ? 'is-active' : ''}
              onClick={() => setAccessMode('view_only')}
            >
              <ViewInArOutlinedIcon />
              <strong>Только готовая сцена</strong>
              <span>Без каталога и посторонних моделей.</span>
            </button>
            <button
              type="button"
              className={form.accessMode === 'mini_calculator' ? 'is-active' : ''}
              onClick={() => setAccessMode('mini_calculator')}
              disabled={!catalogCategories.length}
            >
              <Inventory2OutlinedIcon />
              <strong>Сцена + каталог</strong>
              <span>Каталог метаданных, 3D только по запросу.</span>
            </button>
          </div>

          <label>
            <span>Название доступа</span>
            <input value={form.title} onChange={(event) => setField('title', event.target.value)} />
          </label>

          <div className="sceneGuestAdmin__formRow">
            <label>
              <span>Имя заказчика</span>
              <input value={form.customerName} onChange={(event) => setField('customerName', event.target.value)} />
            </label>
            <label>
              <span>Email заказчика</span>
              <input type="email" value={form.customerEmail} onChange={(event) => setField('customerEmail', event.target.value)} />
            </label>
          </div>

          <div className="sceneGuestAdmin__formRow">
            <label>
              <span>Срок доступа</span>
              <select value={form.duration} onChange={(event) => setField('duration', event.target.value)}>
                <option value="24">1 день</option>
                <option value="72">3 дня</option>
                <option value="168">7 дней</option>
                <option value="720">30 дней</option>
                <option value="none">Без ограничения</option>
              </select>
            </label>
            <label>
              <span>Лимит входов (0 — без ограничения)</span>
              <input type="number" min="0" max="100000" value={form.maxUses} onChange={(event) => setField('maxUses', event.target.value)} />
            </label>
          </div>

          <label className="sceneGuestAdmin__check">
            <input
              type="checkbox"
              checked={form.requiresActiveOfficeSubscription}
              onChange={(event) => setField('requiresActiveOfficeSubscription', event.target.checked)}
            />
            <span>Привязать этот доступ к активной подписке офиса</span>
          </label>

          <label className="sceneGuestAdmin__check">
            <input type="checkbox" checked={form.pinEnabled} onChange={(event) => setField('pinEnabled', event.target.checked)} />
            <span>Защитить PIN-кодом</span>
          </label>

          {form.pinEnabled ? (
            <label>
              <span>PIN</span>
              <input value={form.pin} minLength="4" maxLength="32" required onChange={(event) => setField('pin', event.target.value)} />
            </label>
          ) : null}

          {miniCalculator ? (
            <fieldset className="sceneGuestAdmin__categories">
              <legend>Категории мини-калькулятора</legend>
              <div className="sceneGuestAdmin__categoryActions">
                <button type="button" onClick={() => setField('allowedCategoryIds', catalogCategories.map(cleanId).filter(Boolean))}>Выбрать все</button>
                <button type="button" onClick={() => setField('allowedCategoryIds', [])}>Снять все</button>
              </div>
              <div className="sceneGuestAdmin__categoryGrid">
                {catalogCategories.map((category) => {
                  const categoryId = cleanId(category);
                  return (
                    <label key={categoryId} className="sceneGuestAdmin__check">
                      <input
                        type="checkbox"
                        checked={form.allowedCategoryIds.includes(categoryId)}
                        onChange={() => toggleCategory(categoryId)}
                      />
                      <span>{category.name || 'Категория'}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ) : null}

          <fieldset>
            <legend>Разрешения просмотра</legend>
            <label className="sceneGuestAdmin__check">
              <input type="checkbox" checked={form.walkMode} onChange={(event) => setField('walkMode', event.target.checked)} />
              <span>Прогулка</span>
            </label>
            <label className="sceneGuestAdmin__check">
              <input type="checkbox" checked={form.createScreenshots} onChange={(event) => setField('createScreenshots', event.target.checked)} />
              <span>Создание скриншотов</span>
            </label>
            <label className="sceneGuestAdmin__check">
              <input
                type="checkbox"
                checked={form.downloadScreenshots}
                disabled={!form.createScreenshots}
                onChange={(event) => setField('downloadScreenshots', event.target.checked)}
              />
              <span>Скачивание скриншотов</span>
            </label>
            <label className="sceneGuestAdmin__check">
              <input type="checkbox" checked={form.viewPrices} onChange={(event) => setField('viewPrices', event.target.checked)} />
              <span>Показывать цены</span>
            </label>
            <label className="sceneGuestAdmin__check">
              <input type="checkbox" checked={form.allowRegistrationClaim} onChange={(event) => setField('allowRegistrationClaim', event.target.checked)} />
              <span>Разрешить сохранить проект в аккаунте</span>
            </label>
          </fieldset>

          {miniCalculator ? (
            <fieldset>
              <legend>Права следующего этапа</legend>
              <label className="sceneGuestAdmin__check">
                <input type="checkbox" checked={form.changeQuantity} onChange={(event) => setField('changeQuantity', event.target.checked)} />
                <span>Менять количество</span>
              </label>
              <label className="sceneGuestAdmin__check">
                <input type="checkbox" checked={form.useArrayModifier} onChange={(event) => setField('useArrayModifier', event.target.checked)} />
                <span>Массив</span>
              </label>
              <label className="sceneGuestAdmin__check">
                <input type="checkbox" checked={form.useMirrorModifier} onChange={(event) => setField('useMirrorModifier', event.target.checked)} />
                <span>Зеркало</span>
              </label>
              <div className="sceneGuestAdmin__formRow">
                <label>
                  <span>Максимум элементов массива</span>
                  <input type="number" min="1" max="999" value={form.maxArrayCount} onChange={(event) => setField('maxArrayCount', event.target.value)} />
                </label>
                <label>
                  <span>Товаров на странице каталога</span>
                  <select value={form.catalogPageSize} onChange={(event) => setField('catalogPageSize', Number(event.target.value))}>
                    <option value="8">8</option>
                    <option value="12">12</option>
                    <option value="20">20</option>
                    <option value="30">30</option>
                  </select>
                </label>
              </div>
            </fieldset>
          ) : null}

          <button
            type="submit"
            className="sceneGuestAdmin__create"
            disabled={creating || loadingOptions || !form.officeId || !form.salesSessionId || !options?.scene}
          >
            <AddLinkOutlinedIcon />
            {creating ? 'Создаём…' : 'Создать ссылку и код'}
          </button>
        </form>

        <section className="sceneGuestAdmin__list">
          <div className="sceneGuestAdmin__listHeader">
            <div>
              <span>Выданные доступы</span>
              <h2>{form.salesSessionId || 'Все сессии офиса'}</h2>
            </div>
            <button type="button" onClick={() => loadAccesses(form.officeId, form.salesSessionId)} disabled={loadingAccesses}>
              <RefreshOutlinedIcon />Обновить
            </button>
          </div>

          {accesses.map((item) => (
            <article key={cleanId(item)} className={`sceneGuestAdmin__item is-${item.status}`}>
              <div className="sceneGuestAdmin__itemTop">
                <div>
                  <span>{item.codePreview || 'Код скрыт'}</span>
                  <strong>{item.title || 'Гостевой доступ'}</strong>
                  <small>{item.customerName || item.customerEmail || 'Получатель не указан'}</small>
                </div>
                <b>{item.status === 'active' ? 'Активен' : item.status}</b>
              </div>

              <dl>
                <div><dt>Режим</dt><dd>{item.accessMode === 'mini_calculator' ? 'Мини-калькулятор' : 'Готовая сцена'}</dd></div>
                <div><dt>Сцена</dt><dd>{Number(item.sceneProductIds?.length || 0)} товаров</dd></div>
                <div><dt>Срок</dt><dd>{formatDate(item.expiresAt)}</dd></div>
                <div><dt>Подписка офиса</dt><dd>{item.requiresActiveOfficeSubscription ? 'обязательна' : 'не требуется'}</dd></div>
                <div><dt>Входы</dt><dd>{Number(item.useCount || 0)}{item.maxUses ? ` / ${item.maxUses}` : ''}</dd></div>
                <div><dt>Аккаунт</dt><dd>{item.claimedByUserId ? 'привязан' : 'не привязан'}</dd></div>
              </dl>

              {item.status === 'active' ? (
                <div className="sceneGuestAdmin__itemActions">
                  <button type="button" onClick={() => rotateAccess(item)} disabled={workingId === cleanId(item)}>
                    <LinkOutlinedIcon />Новая ссылка
                  </button>
                  <button type="button" className="is-danger" onClick={() => revokeAccess(item)} disabled={workingId === cleanId(item)}>
                    <BlockOutlinedIcon />Отозвать
                  </button>
                </div>
              ) : null}
            </article>
          ))}

          {!loadingAccesses && !accesses.length ? (
            <div className="sceneGuestAdmin__empty">Гостевые доступы не найдены.</div>
          ) : null}
          {loadingAccesses ? <div className="sceneGuestAdmin__empty">Загружаем доступы…</div> : null}
        </section>
      </div>
    </main>
  );
};

export default SceneGuestAccessAdminPage;
